import { useCallback, useEffect, useState } from "react";
import io from "socket.io-client";
import ioConfig from "../config/ioConfig";
import { SERVER_TO_CLIENT_EVENTS, CLIENT_TO_SERVER_EVENTS } from "../consts";
import { createIdFromMessage } from "../libs/utilities";
export const connectedToServerMessage = "Connected to the server.";
const reconnectTimeout = 3000;
const useSocket = (url, register, getAuthToken, translations) => {
    const [socket, setSocket] = useState(null);
    const [lastMessage, setLastMessage] = useState(null);
    const [message, sendMessage] = useState(null);
    const [conversationId, setConversationId] = useState();
    const [isKicked, setIsKicked] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [isReconnecting, setIsReconnecting] = useState(true);
    const [reconnectCount, setReconnectCount] = useState(50);
    const [connect, setConnect] = useState(false);
    const [isError, setIsError] = useState(false);
    const [typingChanged, setTypingChanged] = useState(false);
    const { connectionFailedText, userNickText, duplicateConnectionText } = translations;
    const handleConnect = useCallback(() => {
        if (socket) {
            socket.emit(CLIENT_TO_SERVER_EVENTS.register, register);
            setIsConnected(true);
        }
    }, [register, socket]);
    const triggerSystemMessage = useCallback((currentMessage) => {
        if (currentMessage === connectedToServerMessage) {
            return;
        }
        setLastMessage({
            id: createIdFromMessage(currentMessage),
            type: "system",
            message: currentMessage,
        });
    }, []);
    const triggerUserMessage = useCallback((currentMessage) => {
        setLastMessage({
            id: createIdFromMessage(currentMessage),
            type: "user",
            nick: userNickText,
            message: currentMessage,
        });
    }, [userNickText]);
    const triggerSupportMessage = useCallback((response) => {
        setLastMessage({
            id: createIdFromMessage(response),
            type: "support",
            conversationId,
            ...response,
        });
    }, [conversationId]);
    const handleRegistered = useCallback((currentConversationId) => {
        setConversationId(currentConversationId);
    }, []);
    const connectToSocket = useCallback(() => {
        setIsKicked(false);
        if (reconnectCount > 0) {
            setReconnectCount(reconnectCount - 1);
            setConnect(true);
        }
        else {
            setIsReconnecting(false);
            setIsError(true);
        }
    }, [setIsError, reconnectCount]);
    const handleConnectionError = useCallback(() => {
        if (isReconnecting) {
            setTimeout(() => {
                connectToSocket();
            }, reconnectTimeout);
        }
        else {
            setReconnectCount(50);
            triggerSystemMessage(connectionFailedText);
        }
    }, [connectToSocket, connectionFailedText, isReconnecting, triggerSystemMessage]);
    const handleFMSDisconnect = useCallback(() => {
        if (socket) {
            socket.disconnect();
        }
    }, [socket]);
    const handleDuplicate = useCallback(() => {
        setIsKicked(true);
        setIsReconnecting(false);
        if (socket) {
            socket.disconnect();
        }
        triggerSystemMessage(duplicateConnectionText);
    }, [duplicateConnectionText, socket, triggerSystemMessage]);
    const handleKicked = useCallback(() => {
        setIsKicked(true);
        setIsReconnecting(false);
        if (socket) {
            socket.disconnect();
        }
        setTimeout(connectToSocket, reconnectTimeout);
    }, [connectToSocket, socket]);
    const handleDisconnect = useCallback(() => {
        if (isKicked) {
            return;
        }
        setIsConnected(false);
        setIsReconnecting(true);
        setTimeout(() => {
            connectToSocket();
        }, reconnectTimeout);
    }, [connectToSocket, isKicked]);
    const handleTypingInfo = useCallback((typingInfo) => {
        setTypingChanged(!!JSON.parse(typingInfo));
    }, []);
    // eslint-disable-next-line @typescript-eslint/ban-types
    const onAuth = useCallback(async (cb) => {
        try {
            cb(await getAuthToken());
        }
        catch (error) {
            cb({ interrupted: true });
        }
    }, [getAuthToken]);
    useEffect(() => {
        setConnect(false);
        const socketIo = io(url, {
            ...ioConfig,
            auth: onAuth,
        });
        setSocket(socketIo);
        return () => {
            socketIo.disconnect();
        };
    }, [url, connect, onAuth]);
    useEffect(() => {
        if (!socket) {
            return;
        }
        socket.on(SERVER_TO_CLIENT_EVENTS.connect, handleConnect);
        socket.on(SERVER_TO_CLIENT_EVENTS.system, triggerSystemMessage);
        socket.on(SERVER_TO_CLIENT_EVENTS.user, triggerUserMessage);
        socket.on(SERVER_TO_CLIENT_EVENTS.registered, handleRegistered);
        socket.on(SERVER_TO_CLIENT_EVENTS.error, handleConnectionError);
        socket.on(SERVER_TO_CLIENT_EVENTS.connect_error, handleConnectionError);
        socket.on(SERVER_TO_CLIENT_EVENTS.connect_failed, handleConnectionError);
        socket.on(SERVER_TO_CLIENT_EVENTS.support, triggerSupportMessage);
        socket.on(SERVER_TO_CLIENT_EVENTS.readd, handleFMSDisconnect);
        socket.on(SERVER_TO_CLIENT_EVENTS.duplicate, handleDuplicate);
        socket.on(SERVER_TO_CLIENT_EVENTS.kick, handleKicked);
        socket.on(SERVER_TO_CLIENT_EVENTS.disconnect, handleDisconnect);
        socket.on(SERVER_TO_CLIENT_EVENTS.typeinfo, handleTypingInfo);
        // eslint-disable-next-line consistent-return
        return () => {
            Object.values(SERVER_TO_CLIENT_EVENTS).forEach((event) => {
                socket.off(event);
            });
        };
    }, [
        handleConnect,
        handleConnectionError,
        handleDisconnect,
        handleDuplicate,
        handleFMSDisconnect,
        handleKicked,
        handleRegistered,
        handleTypingInfo,
        socket,
        triggerSupportMessage,
        triggerSystemMessage,
        triggerUserMessage,
    ]);
    useEffect(() => {
        if (!socket || !message) {
            return;
        }
        const data = { conversationId, message };
        socket.emit(CLIENT_TO_SERVER_EVENTS.sendLine, data);
        sendMessage(null);
    }, [socket, message, conversationId]);
    useEffect(() => {
        if (!isError) {
            return;
        }
        setIsError(false);
        handleConnectionError();
    }, [isError, handleConnectionError, setIsError]);
    return { socket, lastMessage, sendMessage, typingChanged, isKicked, message, isConnected };
};
export default useSocket;
